import React, { useState, useRef } from 'react';
import './PackageBuilder.css';
import BookingConfirmationModal from './BookingConfirmationModal';
import './BookingConfirmationModal.css';

// ═══════════════════════════════════════════════════════════════════════════════
// PACKAGE BUILDER - Trip Type 4: Flights + Hotels
// Carousel-based selection with Split Stay support
// BULLETPROOF VERSION - Handles all edge cases
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE VALUE HELPERS - Prevent rendering objects
// ═══════════════════════════════════════════════════════════════════════════════
const safeString = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') return fallback; // Don't render objects!
  return String(val) || fallback;
};

const safeNumber = (val, fallback = 0) => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number' && !isNaN(val)) return val;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? fallback : parsed;
};

const safeInt = (val, fallback = 0) => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number' && !isNaN(val)) return Math.floor(val);
  const parsed = parseInt(val, 10);
  return isNaN(parsed) ? fallback : parsed;
};

const safeArray = (val) => {
  if (Array.isArray(val)) return val;
  return [];
};

const PackageBuilder = ({ 
  flights = [], 
  hotels = [], 
  searchParams = {},
  hotelParams = {},
  onBook,
  onClose 
}) => {
  // ═══════════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════════
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [selectedHotel1, setSelectedHotel1] = useState(null);
  const [selectedHotel2, setSelectedHotel2] = useState(null);
  const [splitStayMode, setSplitStayMode] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // Hotel date ranges - safely extract
  const initialCheckIn = safeString(hotelParams?.checkIn) || safeString(searchParams?.departureDate) || '';
  const initialCheckOut = safeString(hotelParams?.checkOut) || safeString(searchParams?.returnDate) || '';
  
  const [hotel1Dates, setHotel1Dates] = useState({
    start: initialCheckIn,
    end: initialCheckOut
  });
  const [hotel2Dates, setHotel2Dates] = useState({
    start: '',
    end: initialCheckOut
  });

  // Carousel refs
  const outboundRef = useRef(null);
  const returnRef = useRef(null);
  const hotel1Ref = useRef(null);
  const hotel2Ref = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════════
  // SAFE DATA EXTRACTION
  // ═══════════════════════════════════════════════════════════════════════════════
  const safeFlights = safeArray(flights);
  const safeHotels = safeArray(hotels);
  
  // NOTE: Each flight in allFlights is a COMPLETE round-trip offer containing:
  // - flight.outbound = outbound leg data
  // - flight.return = return leg data (if round trip)
  // - flight.price = total price for the entire trip
  // So we show the SAME flights for both carousels but display different leg data

  const passengers = safeInt(searchParams?.passengers, 1) || 1;
  const hasReturnDate = !!safeString(searchParams?.returnDate);

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Calculate nights
  const calculateNights = (start, end) => {
    if (!start || !end) return 0;
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
      const diff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    } catch {
      return 0;
    }
  };

  const hotel1Nights = calculateNights(hotel1Dates.start, hotel1Dates.end);
  const hotel2Nights = calculateNights(hotel2Dates.start, hotel2Dates.end);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  // Get city name from code
  const getCityName = (code) => {
    const safeCode = safeString(code, '').toUpperCase();
    const cities = {
      'CDG': 'Paris', 'ORY': 'Paris', 'PAR': 'Paris',
      'LHR': 'London', 'LGW': 'London', 'LON': 'London',
      'JFK': 'New York', 'EWR': 'New York', 'LGA': 'New York', 'NYC': 'New York',
      'LAX': 'Los Angeles', 'DXB': 'Dubai', 'SIN': 'Singapore',
      'HND': 'Tokyo', 'NRT': 'Tokyo', 'TYO': 'Tokyo',
      'IAH': 'Houston', 'HOU': 'Houston',
      'SFO': 'San Francisco', 'MIA': 'Miami', 'ORD': 'Chicago',
      'BKK': 'Bangkok', 'HKG': 'Hong Kong', 'FCO': 'Rome', 'ROM': 'Rome'
    };
    return cities[safeCode] || safeCode || 'Destination';
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SAFE PRICE EXTRACTION - Handle price as object or number
  // ═══════════════════════════════════════════════════════════════════════════════
  const getFlightPrice = (flight) => {
    if (!flight) return 0;
    // Price can be object {total, base, perPerson} or direct number
    if (typeof flight.price === 'object' && flight.price !== null) {
      return safeNumber(flight.price.total) || safeNumber(flight.price.perPerson) || safeNumber(flight.price.amount) || 0;
    }
    return safeNumber(flight.price) || safeNumber(flight.totalPrice) || 0;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // PRICE CALCULATIONS (all safe)
  // NOTE: For round trips, API returns complete offers with total price for both legs
  // The outbound and return carousels show the same flights with different leg displays
  // Price is the total for the selected flight offer
  // ═══════════════════════════════════════════════════════════════════════════════
  const selectedFlightPrice = getFlightPrice(selectedOutbound) || getFlightPrice(selectedReturn);
  const flightTotal = selectedFlightPrice * passengers;

  const hotel1PricePerNight = safeNumber(selectedHotel1?.price) || safeNumber(selectedHotel1?.pricePerNight);
  const hotel2PricePerNight = splitStayMode ? (safeNumber(selectedHotel2?.price) || safeNumber(selectedHotel2?.pricePerNight)) : 0;
  const hotel1Price = hotel1PricePerNight * hotel1Nights;
  const hotel2Price = hotel2PricePerNight * hotel2Nights;
  const hotelTotal = hotel1Price + hotel2Price;

  const grandTotal = flightTotal + hotelTotal;

  // Check if package is complete
  // Check if this is a round trip (has return date AND flights have return leg data)
  const hasReturnFlights = safeFlights.some(f => f?.return);
  const isRoundTrip = hasReturnDate && hasReturnFlights;
  const flightsComplete = isRoundTrip ? (selectedOutbound && selectedReturn) : !!selectedOutbound;
  const hotelsComplete = splitStayMode ? (selectedHotel1 && selectedHotel2) : !!selectedHotel1;
  const packageComplete = flightsComplete && hotelsComplete;

  // ═══════════════════════════════════════════════════════════════════════════════
  // CAROUSEL SCROLL
  // ═══════════════════════════════════════════════════════════════════════════════
  const scrollCarousel = (ref, direction) => {
    try {
      if (ref?.current) {
        const card = ref.current.querySelector('.flight-card, .hotel-card');
        const cardWidth = card?.offsetWidth || 320;
        ref.current.scrollBy({ left: direction * (cardWidth + 16), behavior: 'smooth' });
      }
    } catch {
      // Ignore scroll errors
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SPLIT STAY TOGGLE
  // ═══════════════════════════════════════════════════════════════════════════════
  const toggleSplitStay = () => {
    if (!splitStayMode) {
      // Entering split mode - divide dates in half
      const totalNights = calculateNights(hotel1Dates.start, hotel1Dates.end);
      const midNights = Math.floor(totalNights / 2);
      
      if (hotel1Dates.start && totalNights > 1) {
        try {
          const startDate = new Date(hotel1Dates.start);
          const midDate = new Date(startDate);
          midDate.setDate(midDate.getDate() + midNights);
          
          const midDateStr = midDate.toISOString().split('T')[0];
          
          setHotel1Dates({ start: hotel1Dates.start, end: midDateStr });
          setHotel2Dates({ start: midDateStr, end: hotel1Dates.end });
        } catch {
          // Keep current dates if calculation fails
        }
      }
      setSplitStayMode(true);
    } else {
      // Exiting split mode - restore full date range
      setHotel1Dates({
        start: initialCheckIn,
        end: initialCheckOut
      });
      setSelectedHotel2(null);
      setSplitStayMode(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // BOOKING
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Build package data for confirmation modal
  const buildPackageData = () => ({
    outboundFlight: selectedOutbound,
    returnFlight: selectedReturn,
    hotels: splitStayMode 
      ? [
          { ...selectedHotel1, dates: hotel1Dates, nights: hotel1Nights },
          { ...selectedHotel2, dates: hotel2Dates, nights: hotel2Nights }
        ]
      : [{ ...selectedHotel1, dates: hotel1Dates, nights: hotel1Nights }],
    flightTotal,
    hotelTotal,
    grandTotal,
    passengers
  });

  const handleBook = () => {
    if (!packageComplete) return;
    // Show the confirmation modal with individual book buttons
    setShowConfirmationModal(true);
    
    // Also call onBook if provided (for analytics/tracking)
    if (onBook) {
      try {
        onBook(buildPackageData());
      } catch {
        // Ignore errors
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmationModal(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER FLIGHT CARD - SAFE
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderFlightCard = (flight, isSelected, onSelect, isReturn = false) => {
    if (!flight) return null;
    
    try {
      // Get the leg data (outbound or return)
      const leg = isReturn ? (flight.return || flight.outbound) : flight.outbound;
      
      // Airline info - can be object {code, name, logo} or string
      let airlineName = 'Airline';
      let airlineCode = '';
      if (typeof flight.airline === 'object' && flight.airline !== null) {
        airlineName = safeString(flight.airline.name) || safeString(flight.airline.code) || 'Airline';
        airlineCode = safeString(flight.airline.code);
      } else {
        airlineName = safeString(flight.airline) || safeString(flight.carrierName) || 'Airline';
        airlineCode = safeString(flight.carrierCode) || '';
      }
      
      // Flight number
      const flightNum = leg?.flightNumbers?.[0] || safeString(flight.flightNumber) || airlineCode || '';
      
      // Stops
      const stops = safeInt(leg?.stops) ?? safeInt(flight.stops) ?? 0;
      const isNonstop = stops === 0;
      
      // Format ISO datetime to time string
      const formatTime = (isoString) => {
        if (!isoString) return '--:--';
        try {
          const date = new Date(isoString);
          if (isNaN(date.getTime())) return '--:--';
          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch {
          return '--:--';
        }
      };
      
      // Extract times - can be ISO string or already formatted
      let depTime, arrTime;
      if (leg?.departure) {
        depTime = formatTime(leg.departure);
        arrTime = formatTime(leg.arrival);
      } else {
        depTime = safeString(flight.departureTime) || safeString(flight.departure?.time) || '--:--';
        arrTime = safeString(flight.arrivalTime) || safeString(flight.arrival?.time) || '--:--';
      }
      
      // Airports
      const depAirport = safeString(leg?.origin) || safeString(flight.departureAirport) || safeString(flight.departure?.airport) || safeString(flight.origin) || safeString(searchParams?.origin) || '---';
      const arrAirport = safeString(leg?.destination) || safeString(flight.arrivalAirport) || safeString(flight.arrival?.airport) || safeString(flight.destination) || safeString(searchParams?.destination) || '---';
      
      // Duration - format PT2H30M to "2h 30m"
      const formatDuration = (dur) => {
        if (!dur) return '';
        if (typeof dur === 'string' && dur.startsWith('PT')) {
          const hours = dur.match(/(\d+)H/)?.[1] || '0';
          const mins = dur.match(/(\d+)M/)?.[1] || '0';
          return `${hours}h ${mins}m`;
        }
        return safeString(dur);
      };
      
      const duration = formatDuration(leg?.duration) || safeString(flight.duration) || safeString(flight.totalDuration) || '';
      const price = getFlightPrice(flight);

      return (
        <div 
          className={`flight-card ${isSelected ? 'selected' : ''}`}
          onClick={() => onSelect(flight)}
        >
          {isSelected && <div className="selected-badge">✓ Selected</div>}
          
          <div className="flight-card-header">
            <div className="airline-logo">{isReturn ? '🛬' : '🛫'}</div>
            <div className="airline-info">
              <div className="airline-name">{airlineName}</div>
              <div className="flight-number">{flightNum} • {isNonstop ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}</div>
            </div>
          </div>

          <div className="flight-card-body">
            <div className="flight-times">
              <div className="time-block">
                <div className="time">{depTime}</div>
                <div className="airport">{depAirport}</div>
              </div>
              
              <div className="flight-path">
                <div className="duration">{duration}</div>
                <div className="path-line">
                  <span className="plane-icon">✈️</span>
                </div>
                <span className={`stops-badge ${isNonstop ? 'nonstop' : 'has-stops'}`}>
                  {isNonstop ? 'Nonstop' : `${stops} stop`}
                </span>
              </div>
              
              <div className="time-block">
                <div className="time">{arrTime}</div>
                <div className="airport">{arrAirport}</div>
              </div>
            </div>
          </div>

          <div className="flight-card-footer">
            <div className="flight-date">{isReturn ? 'Return' : 'Depart'}</div>
            <div className="flight-price">
              <div className="price-label">per person</div>
              <div className="price-amount">${price.toLocaleString()}</div>
            </div>
          </div>
        </div>
      );
    } catch (err) {
      console.error('Flight card render error:', err);
      return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER HOTEL CARD - SAFE
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderHotelCard = (hotel, isSelected, onSelect, nights) => {
    if (!hotel) return null;
    
    try {
      const name = safeString(hotel.name) || safeString(hotel.hotelName) || 'Hotel';
      const rating = safeNumber(hotel.rating) || safeNumber(hotel.guestRating) || 4.0;
      const stars = safeInt(hotel.stars) || safeInt(hotel.starRating) || 4;
      const price = safeNumber(hotel.price) || safeNumber(hotel.pricePerNight) || 0;
      
      // Location can be string or object - handle safely
      let locationText = '';
      const distanceToCenter = safeNumber(hotel.distanceToCenter) || safeNumber(hotel.distance);
      
      if (distanceToCenter > 0) {
        locationText = `${distanceToCenter.toFixed(1)} mi to center`;
      } else if (typeof hotel.location === 'string') {
        locationText = hotel.location;
      } else if (typeof hotel.address === 'string') {
        locationText = hotel.address;
      } else {
        locationText = 'City Center Area';
      }
      
      // Amenities - filter to only strings
      const rawAmenities = safeArray(hotel.amenities);
      const amenities = rawAmenities
        .filter(a => typeof a === 'string')
        .slice(0, 3);

      // Rating label
      const getRatingLabel = (r) => {
        if (r >= 4.5) return 'Exceptional';
        if (r >= 4.0) return 'Excellent';
        if (r >= 3.5) return 'Very Good';
        if (r >= 3.0) return 'Good';
        return 'Fair';
      };

      // Color based on rating
      const getGradient = (r) => {
        if (r >= 4.5) return 'linear-gradient(135deg, #f59e0b, #d97706)';
        if (r >= 4.0) return 'linear-gradient(135deg, #10b981, #059669)';
        if (r >= 3.5) return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
        return 'linear-gradient(135deg, #6366f1, #4f46e5)';
      };

      const safeNights = safeInt(nights);
      const totalPrice = price * safeNights;

      return (
        <div 
          className={`hotel-card ${isSelected ? 'selected' : ''}`}
          onClick={() => onSelect(hotel)}
        >
          {isSelected && <div className="selected-badge">✓ Selected</div>}
          
          <div className="hotel-image" style={{ background: getGradient(rating) }}>
            🏨
            <div className="hotel-stars">{'⭐'.repeat(Math.min(Math.max(stars, 1), 5))}</div>
            <div className="hotel-rating-badge">
              <span className="rating-score">{rating.toFixed(1)}</span>
              <span className="rating-label">{getRatingLabel(rating)}</span>
            </div>
          </div>

          <div className="hotel-content">
            <div className="hotel-name">{name}</div>
            <div className="hotel-location">📍 {locationText}</div>
            
            {amenities.length > 0 && (
              <div className="hotel-amenities">
                {amenities.map((amenity, i) => (
                  <span key={i} className="amenity-tag">{amenity}</span>
                ))}
              </div>
            )}

            <div className="hotel-footer">
              <div className="hotel-price-info">
                <div className="hotel-price">${price.toLocaleString()}<span>/night</span></div>
                {safeNights > 0 && (
                  <div className="hotel-total">${totalPrice.toLocaleString()} total for {safeNights} nights</div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } catch (err) {
      console.error('Hotel card render error:', err);
      return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  const destinationCity = getCityName(searchParams?.destination);
  const departureDate = formatDate(safeString(searchParams?.departureDate));
  const returnDate = formatDate(safeString(searchParams?.returnDate));

  return (
    <div className="package-builder">
      
      {/* ═══════════════════════════════════════════════════════════════════════════════
          RUNNING TOTAL (Sticky)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      <div className="running-total">
        <div className="running-total-left">
          <div className="running-total-title">
            📦 Your Package Total
          </div>
          <div className="running-total-breakdown">
            {packageComplete 
              ? `Flights: $${flightTotal.toLocaleString()} + Hotels: $${hotelTotal.toLocaleString()}`
              : 'Select outbound flight, return flight, hotel'}
          </div>
        </div>
        
        <div className="running-total-right">
          <div className="running-total-amount">${grandTotal.toLocaleString()}</div>
          {packageComplete && (
            <div className="running-total-savings">💰 Bundle & save!</div>
          )}
          <button 
            className="book-package-btn"
            disabled={!packageComplete}
            onClick={handleBook}
          >
            {packageComplete ? 'Book Package →' : 'Select All to Book →'}
          </button>
        </div>

        {onClose && (
          <button className="close-package-btn" onClick={onClose}>✕</button>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════════
          OUTBOUND FLIGHTS
          ═══════════════════════════════════════════════════════════════════════════════ */}
      {safeFlights.length > 0 && (
        <>
          <div className="section-header">
            <div className="section-title">
              ✈️ Outbound Flight 
              {departureDate && <span className="section-date">{departureDate}</span>}
            </div>
            <div className={`section-badge ${selectedOutbound ? 'selected' : ''}`}>
              {selectedOutbound ? '✓ Selected' : 'Select one'}
            </div>
          </div>

          <div className="carousel-container">
            <button className="carousel-nav prev" onClick={() => scrollCarousel(outboundRef, -1)}>←</button>
            <div className="carousel-track" ref={outboundRef}>
              {safeFlights.map((flight, i) => (
                <React.Fragment key={`outbound-${i}`}>
                  {renderFlightCard(flight, selectedOutbound === flight, setSelectedOutbound, false)}
                </React.Fragment>
              ))}
            </div>
            <button className="carousel-nav next" onClick={() => scrollCarousel(outboundRef, 1)}>→</button>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          RETURN FLIGHTS (Only for round trips with return data)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      {hasReturnDate && safeFlights.length > 0 && safeFlights.some(f => f?.return) && (
        <>
          <div className="section-header">
            <div className="section-title">
              ✈️ Return Flight 
              {returnDate && <span className="section-date">{returnDate}</span>}
            </div>
            <div className={`section-badge ${selectedReturn ? 'selected' : ''}`}>
              {selectedReturn ? '✓ Selected' : 'Select one'}
            </div>
          </div>

          <div className="carousel-container">
            <button className="carousel-nav prev" onClick={() => scrollCarousel(returnRef, -1)}>←</button>
            <div className="carousel-track" ref={returnRef}>
              {safeFlights.filter(f => f?.return).map((flight, i) => (
                <React.Fragment key={`return-${i}`}>
                  {renderFlightCard(flight, selectedReturn === flight, setSelectedReturn, true)}
                </React.Fragment>
              ))}
            </div>
            <button className="carousel-nav next" onClick={() => scrollCarousel(returnRef, 1)}>→</button>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          HOTELS SECTION
          ═══════════════════════════════════════════════════════════════════════════════ */}
      {safeHotels.length > 0 && (
        <>
          <div className="hotel-section-header">
            <div className="hotel-section-left">
              <div className="section-title">🏨 Hotels in {destinationCity}</div>
              <div className={`section-badge ${selectedHotel1 ? 'selected' : ''}`}>
                {splitStayMode 
                  ? (selectedHotel1 && selectedHotel2 ? '✓ Selected' : 'Select hotels below')
                  : (selectedHotel1 ? '✓ Selected' : 'Select one')}
              </div>
            </div>
            <button 
              className={`split-stay-btn ${splitStayMode ? 'active' : ''}`}
              onClick={toggleSplitStay}
            >
              {splitStayMode ? '✓ Split Stay Active' : '✨ Split Stay'}
            </button>
          </div>

          {/* Split Stay Indicator */}
          {splitStayMode && (
            <div className="split-stay-indicator">
              <div className="split-stay-indicator-icon">🏨🏨</div>
              <div className="split-stay-indicator-text">
                <div className="split-stay-indicator-title">Split Stay Mode Active</div>
                <div className="split-stay-indicator-subtitle">Select hotels for each part of your trip</div>
              </div>
              <button className="cancel-split-btn" onClick={toggleSplitStay}>Cancel Split</button>
            </div>
          )}

          {/* Hotel Segment 1 */}
          <div className={`hotel-segment ${!splitStayMode ? 'active' : ''}`}>
            {splitStayMode && (
              <div className="hotel-segment-header">
                <div className="hotel-segment-title">
                  <span className="hotel-segment-number">1</span>
                  First Hotel
                </div>
              </div>
            )}

            <div className="date-range-selector">
              <div className="date-range-label">📅 Stay dates:</div>
              <div className="date-inputs">
                <input 
                  type="date" 
                  className="date-input" 
                  value={hotel1Dates.start}
                  onChange={(e) => setHotel1Dates(prev => ({ ...prev, start: e.target.value }))}
                />
                <span className="date-separator">to</span>
                <input 
                  type="date" 
                  className="date-input" 
                  value={hotel1Dates.end}
                  onChange={(e) => setHotel1Dates(prev => ({ ...prev, end: e.target.value }))}
                />
              </div>
              <div className="nights-badge">{hotel1Nights} nights</div>
            </div>

            <div className="carousel-container">
              <button className="carousel-nav prev" onClick={() => scrollCarousel(hotel1Ref, -1)}>←</button>
              <div className="carousel-track" ref={hotel1Ref}>
                {safeHotels.map((hotel, i) => (
                  <React.Fragment key={`hotel1-${i}`}>
                    {renderHotelCard(hotel, selectedHotel1 === hotel, setSelectedHotel1, hotel1Nights)}
                  </React.Fragment>
                ))}
              </div>
              <button className="carousel-nav next" onClick={() => scrollCarousel(hotel1Ref, 1)}>→</button>
            </div>
          </div>

          {/* Hotel Segment 2 (Split Stay only) */}
          {splitStayMode && (
            <div className="hotel-segment">
              <div className="hotel-segment-header">
                <div className="hotel-segment-title">
                  <span className="hotel-segment-number">2</span>
                  Second Hotel
                </div>
                <button className="remove-hotel-btn" onClick={toggleSplitStay}>✕</button>
              </div>

              <div className="date-range-selector">
                <div className="date-range-label">📅 Stay dates:</div>
                <div className="date-inputs">
                  <input 
                    type="date" 
                    className="date-input" 
                    value={hotel2Dates.start}
                    onChange={(e) => setHotel2Dates(prev => ({ ...prev, start: e.target.value }))}
                  />
                  <span className="date-separator">to</span>
                  <input 
                    type="date" 
                    className="date-input" 
                    value={hotel2Dates.end}
                    onChange={(e) => setHotel2Dates(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                <div className="nights-badge">{hotel2Nights} nights</div>
              </div>

              <div className="carousel-container">
                <button className="carousel-nav prev" onClick={() => scrollCarousel(hotel2Ref, -1)}>←</button>
                <div className="carousel-track" ref={hotel2Ref}>
                  {safeHotels.map((hotel, i) => (
                    <React.Fragment key={`hotel2-${i}`}>
                      {renderHotelCard(hotel, selectedHotel2 === hotel, setSelectedHotel2, hotel2Nights)}
                    </React.Fragment>
                  ))}
                </div>
                <button className="carousel-nav next" onClick={() => scrollCarousel(hotel2Ref, 1)}>→</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          PACKAGE SUMMARY (Shows when complete)
          ═══════════════════════════════════════════════════════════════════════════════ */}
      {packageComplete && (
        <div className="package-summary">
          <div className="package-summary-header">
            <h3>📦 Your Package Summary</h3>
          </div>
          
          <div className="package-items">
            <div className="package-item">
              <div className="package-item-icon flight">✈️</div>
              <div className="package-item-details">
                <div className="package-item-label">Flights ({passengers} pax)</div>
                <div className="package-item-value">
                  {safeString(selectedOutbound?.airline?.name) || safeString(selectedOutbound?.airline) || 'Flight'} 
                  {isRoundTrip && ' (Round Trip)'}
                </div>
              </div>
              <div className="package-item-price">${flightTotal.toLocaleString()}</div>
            </div>

            <div className="package-item">
              <div className="package-item-icon hotel">🏨</div>
              <div className="package-item-details">
                <div className="package-item-label">
                  {splitStayMode ? 'Hotel 1' : 'Hotel'} ({hotel1Nights} nights)
                </div>
                <div className="package-item-value">{safeString(selectedHotel1?.name, 'Hotel')}</div>
              </div>
              <div className="package-item-price">${hotel1Price.toLocaleString()}</div>
            </div>

            {splitStayMode && selectedHotel2 && (
              <div className="package-item">
                <div className="package-item-icon hotel">🏨</div>
                <div className="package-item-details">
                  <div className="package-item-label">Hotel 2 ({hotel2Nights} nights)</div>
                  <div className="package-item-value">{safeString(selectedHotel2?.name, 'Hotel')}</div>
                </div>
                <div className="package-item-price">${hotel2Price.toLocaleString()}</div>
              </div>
            )}
          </div>

          <div className="package-summary-total">
            <div className="total-label">Package Total</div>
            <div className="total-amount">${grandTotal.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Booking Confirmation Modal */}
      <BookingConfirmationModal
        isOpen={showConfirmationModal}
        onClose={handleCloseConfirmation}
        packageData={buildPackageData()}
        searchParams={searchParams}
        hotelParams={hotelParams}
      />

    </div>
  );
};

export default PackageBuilder;
